

/**
=========================================================
* Material Dashboard 2 PRO React - v2.2.1
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import { v2 as t, v2Tones } from "styles/tokens/v2";

// UI v2: quiet outlined toolbar buttons, a segmented view switch, tinted
// "today" and the v2 border and text colours. Legacy styles stay as they are.
const v2CalendarStyles = {
  "& .fc .fc-toolbar.fc-header-toolbar": { marginBottom: "16px" },
  "& .fc .fc-toolbar-chunk": { display: "flex", alignItems: "center", gap: "8px" },
  "& .fc-button, .fc-today-button, .fc-button:disabled": {
    backgroundColor: `${t.surface} !important`,
    borderColor: `${t.border2} !important`,
    color: `${t.text} !important`,
    fontFamily: "inherit !important",
    fontSize: "13px !important",
    fontWeight: "500 !important",
    textTransform: "capitalize !important",
    height: "34px",
    padding: "0 12px !important",
    boxShadow: "none !important",
    opacity: "1 !important",
    "&:hover": { backgroundColor: `${t.hover} !important`, transform: "none" },
    "&:focus-visible": { boxShadow: `0 0 0 2px ${t.link} !important` },
    "&:focus, &:active": { transform: "none" },
  },
  "& .fc-button:disabled": { color: `${t.textDisabled} !important` },
  "& .fc .fc-button-group > .fc-button": { minWidth: "34px" },
  "& .fc .fc-button.fc-button-active": {
    backgroundColor: `${v2Tones.info.bg} !important`,
    color: `${v2Tones.info.fg} !important`,
    fontWeight: "600 !important",
  },
  "& .fc-toolbar-title": { fontSize: "18px !important", fontWeight: "600 !important", color: `${t.text} !important` },
  "& .fc-theme-standard td, .fc-theme-standard th": { borderColor: t.border },
  "& .fc .fc-col-header-cell-cushion": {
    fontSize: "12px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: t.text3,
  },
  "& .fc .fc-daygrid-day-number": { color: t.text2, fontSize: "13px", fontWeight: 500 },
  "& .fc .fc-day-today": { backgroundColor: "#F0F6FE !important" },
  "& .fc .fc-day-today .fc-daygrid-day-number": { color: v2Tones.info.fg, fontWeight: 700 },
  "& .fc .fc-timegrid-now-indicator-line": { borderColor: v2Tones.danger.fg },
};
// Applied after the legacy event-* gradients so the page's own event chips
// show through instead of a solid colour block.
const v2EventStyles = {
  "& .fc .fc-event[class*='event-']": { backgroundImage: "none", backgroundColor: "transparent", borderColor: "transparent" },
};

export default styled(Box)(({ theme, ownerState }) => {
  const { palette, typography, functions, boxShadows } = theme;
  const { darkMode } = ownerState;

  const { white, dark, light, grey, gradients, info } = palette;
  const { size, fontWeightMedium, fontWeightBold, fontWeightRegular, fontWeightLight } = typography;
  const { linearGradient, pxToRem } = functions;
  const { md } = boxShadows;

  return {
    height: "100%",

    "& .fc-media-screen": {
      height: "100%",
      color: dark.main,
    },

    "& .fc-theme-standard .fc-scrollgrid": {
      border: "none",
    },

    "& .fc-theme-standard thead tr th": {
      borderLeft: "none",
      borderRight: "none",
    },

    "& .fc-theme-standard td, .fc-theme-standard th": {
      borderColor: light.main,
    },

    "& .fc th": {
      textAlign: "center",
    },

    "& .fc .fc-col-header-cell-cushion": {
      fontSize: size.sm,
      fontWeight: fontWeightMedium,
      color: darkMode ? white.main : grey[500],
    },

    "& .fc .fc-daygrid-day-number": {
      color: darkMode ? white.main : grey[700],
      fontSize: size.sm,
      fontWeight: fontWeightLight,
      width: "100%",
      textAlign: "center",
    },

    "& .fc-scrollgrid-section.fc-scrollgrid-section-header > td": {
      border: "none",
    },

    "& .fc-scrollgrid-section.fc-scrollgrid-section-body.fc-scrollgrid-section-liquid > td": {
      border: "none",
    },

    "& .fc-scrollgrid-sync-table": {
      height: "auto !important",
    },

    "& .fc .fc-view-harness-active > .fc-view": {
      position: "static",
      height: "100%",
    },

    "& .fc .fc-scroller-liquid-absolute": {
      position: "static",
    },

    "& .fc-daygrid-event": {
      margin: `${pxToRem(0.5)} ${pxToRem(2)}`,
      border: "none",
      borderRadius: pxToRem(5.6),
      fontSize: size.sm,
      fontWeight: fontWeightMedium,
    },

    "& .fc .fc-daygrid-body-unbalanced .fc-daygrid-day-events": {
      minHeight: pxToRem(32),
    },

    "& .fc-event-title": {
      fontSize: `${size.xs} !important`,
      fontWeight: `${fontWeightRegular} !important`,
      padding: `${pxToRem(2)} ${pxToRem(4.8)} ${pxToRem(1.5)} !important`,
    },

    "& .fc-button, .fc-today-button, .fc-button:disabled": {
      backgroundColor: `${info.main} !important`,
      borderColor: `${info.main} !important`,
      fontSize: `${size.sm} !important`,
      boxShadow: `${md} !important`,
      opacity: "1 !important",
      transition: `all 150ms ease-in`,

      "&:hover, &:focus, &:active": {
        transform: "scale(1.02)",
        boxShadow: `${md} !important`,
        backgroundColor: `${info.main} !important`,
        borderColor: `${info.main} !important`,
      },
    },

    "& .fc .fc-button .fc-icon": {
      fontSize: size.sm,
    },

    "& .fc-toolbar-title": {
      fontSize: `${size.lg} !important`,
      fontWeight: `${fontWeightBold} !important`,
      color: darkMode ? white.main : dark.main,
    },

    "& .event-primary": {
      backgroundImage: linearGradient(gradients.primary.main, gradients.primary.state),
      "& *": { color: white.main },
    },

    "& .event-secondary": {
      backgroundImage: linearGradient(gradients.secondary.main, gradients.secondary.state),
      "& *": { color: white.main },
    },

    "& .event-info": {
      backgroundImage: linearGradient(gradients.info.main, gradients.info.state),
      "& *": { color: white.main },
    },

    "& .event-success": {
      backgroundImage: linearGradient(gradients.success.main, gradients.success.state),
      "& *": { color: white.main },
    },

    "& .event-warning": {
      backgroundImage: linearGradient(gradients.warning.main, gradients.warning.state),
      "& *": { color: white.main },
    },

    "& .event-error": {
      backgroundImage: linearGradient(gradients.error.main, gradients.error.state),
      "& *": { color: white.main },
    },

    "& .event-light": {
      backgroundImage: linearGradient(gradients.light.main, gradients.light.state),

      "& *": { color: dark.main },
    },

    "& .event-dark": {
      backgroundImage: linearGradient(gradients.dark.main, gradients.dark.state),
      "& *": { color: white.main },
    },
    ...(theme.pearlUi === "v2" ? { ...v2CalendarStyles, ...v2EventStyles } : {}),
  };
});
